import {
  BaseState,
  IResponse,
  IPaginate,
  IPagination,
  IGroup,
} from '@/types/types';
import { RootState, IContact } from '../../types/types';
import { MutationTree, ActionTree, GetterTree, Module } from 'vuex';
import { isNetworkError } from '@/utils/helpers';
import { of } from 'rxjs';
import {
  addContactsToGroup,
  addGroup,
  details,
  groupContacts,
  groups,
  removeContactsFromGroup,
  removeGroup,
  uploadContactToGroup,
} from '@/services/groups.service';
import { updateGroup } from '@/services/groups.service';
import qs from 'qs';
const namespaced = true;
export type Paginate = {
  paginate: {
    total: number;
    page: number;
    limit: number;
  };
};

const state: BaseState<IGroup> = {
  list: [],
  groupContacts: [],
  details: {
    id: 0,
    name: '',
    description: '',
  },
  paginate: {
    total: 0,
    page: 1,
    limit: 30,
  },
  contactsPaginate: {
    total: 0,
    page: 1,
    limit: 30,
    pages: 1,
  },
  reset: {
    name: '',
  },
  searchResults: [],
};

const mutations: MutationTree<BaseState<IGroup>> = {
  UPDATE_LIST(state, payload: ReadonlyArray<IGroup>) {
    state.list = payload;
  },
  UPDATE_GROUP_CONTACTS(state, payload: ReadonlyArray<IContact>) {
    state.groupContacts = payload;
  },
  UPDATE_DETAILS(state, payload: IGroup) {
    state.details = payload;
  },
  UPDATE_SEARCH_RESULTS(state, payload: ReadonlyArray<IGroup>) {
    state.searchResults = payload;
  },
  UPDATE_PAGINATION(
    state,
    payload: Pick<IPaginate, 'total' | 'page' | 'itemsPerPage'>
  ) {
    state.paginate = {
      page: payload.page,
      total: payload.total,
      limit: payload.itemsPerPage,
    };
  },
  UPDATE_CONTACTS_PAGINATION(
    state,
    payload: Pick<IPaginate, 'total' | 'page' | 'itemsPerPage' | 'pages'>
  ) {
    state.contactsPaginate = {
      page: payload.page,
      total: payload.total,
      limit: payload.itemsPerPage,
      pages: payload.pages,
    };
  },
};

const actions: ActionTree<BaseState<IGroup>, RootState> = {
  async rawList({ dispatch, commit }) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const query = qs.stringify(
        {
          page: 1,
          limit: 12,
          isPaginated: false,
        },
        { skipNulls: true, addQueryPrefix: true }
      );
      const response$ = of<IResponse<IGroup>>(await groups(query));
      response$.subscribe((contacts) => {
        commit('UPDATE_LIST', contacts.data);
        dispatch('isPageLoading', false, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async list({ commit, dispatch }, payload) {
    try {
      dispatch('isPageLoading', true, { root: true });
      const { page, limit, searchTerm } = payload;
      const query = qs.stringify(
        {
          page,
          limit,
          isPaginated: true,
          searchTerm,
        },
        { skipNulls: true, addQueryPrefix: true }
      );
      const response$ = of<IResponse<IPagination<IGroup>>>(await groups(query));
      response$.subscribe((groups) => {
        const { page, limit, total, docs } = groups.data.paginateObj;
        commit('UPDATE_LIST', docs);
        commit('UPDATE_PAGINATION', { page, total, itemsPerPage: limit });
        dispatch('isPageLoading', false, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isPageLoading', false, { root: true });
    }
  },
  async details({ commit, dispatch }, id) {
    try {
      dispatch('isDialogLoading', true, { root: true });
      const response$ = of<IResponse<IGroup>>(await details(id));
      response$.subscribe((contacts) => {
        commit('UPDATE_DETAILS', contacts.data);
        dispatch('isDialogLoading', false, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isDialogLoading', false, { root: true });
    }
  },
  async create({ state, dispatch }, payload: IGroup) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<IResponse<IGroup>>(await addGroup(payload));
      response$.subscribe(() => {
        dispatch('isLoading', false, { root: true });
        dispatch('list', {
          page: (state.paginate as IPaginate).page,
          limit: (state.paginate as IPaginate & { limit: number }).limit,
        });
        dispatch('snackBarVisibility', true, {
          root: true,
        });
        dispatch('snackBarMessage', `Group added successfully`, {
          root: true,
        });
        dispatch('updateDialog', { idx: 'add', state: false }, { root: true });
        dispatch('resetFormValues', true, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async update({ state, dispatch }, payload: IGroup) {
    try {
      dispatch('isLoading', true, { root: true });
      const { id, ...rest } = payload;
      const response$ = of<IResponse<IGroup>>(
        await updateGroup(Number(id), rest)
      );
      response$.subscribe(() => {
        dispatch('isLoading', false, { root: true });
        dispatch('list', {
          page: (state.paginate as IPaginate).page,
          limit: (state.paginate as IPaginate & { limit: number }).limit,
        });
        dispatch('snackBarVisibility', true, {
          root: true,
        });
        dispatch('snackBarMessage', `Group updated successfully`, {
          root: true,
        });
        dispatch('updateDialog', { idx: 'edit', state: false }, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async delete({ dispatch, state }, id) {
    try {
      dispatch('isLoading', true, { root: true });
      const response$ = of<IResponse<IGroup>>(await removeGroup(id));
      response$.subscribe(() => {
        dispatch('isLoading', false, { root: true });
        dispatch('list', {
          page: (state.paginate as IPaginate).page,
          limit: (state.paginate as IPaginate & { limit: number }).limit,
        });
        dispatch('snackBarVisibility', true, {
          root: true,
        });
        dispatch('snackBarMessage', `Group deleted successfully`, {
          root: true,
        });

        dispatch(
          'updateDialog',
          { idx: 'delete', state: false },
          { root: true }
        );
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async bulkUpload({ dispatch }, payload: { id: number; file: FormData }) {
    try {
      dispatch('isLoading', true, { root: true });
      const { id, ...rest } = payload;
      const response$ = of<IResponse<IGroup>>(
        await uploadContactToGroup(id, rest.file)
      );
      response$.subscribe(() => {
        dispatch('isLoading', false, { root: true });
        dispatch('list', {
          page: 1,
          limit: 30,
        });
        dispatch('snackBarVisibility', true, {
          root: true,
        });
        dispatch('snackBarMessage', `Contacts successfully upload to group`, {
          root: true,
        });
        dispatch(
          'updateDialog',
          { idx: 'upload', state: false },
          { root: true }
        );
        dispatch('resetFormValues', true, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async assignContactsToGroup(
    { dispatch, state },
    payload: { groupId: number; contacts: IContact[] }
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      const { groupId, ...rest } = payload;
      const response$ = of<IResponse<IGroup>>(
        await addContactsToGroup(groupId, rest)
      );
      response$.subscribe(() => {
        dispatch('isLoading', false, { root: true });
        dispatch('list', {
          page: (state.paginate as IPaginate).page,
          limit: (state.paginate as IPaginate & { limit: number }).limit,
        });
        dispatch('snackBarVisibility', true, {
          root: true,
        });
        dispatch('snackBarMessage', `Contacts assigned to group successfully`, {
          root: true,
        });
        dispatch(
          'updateDialog',
          { idx: 'contacts', state: false },
          { root: true }
        );
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async removeContactsFromGroup(
    { dispatch },
    payload: { groupId: number; contacts: IContact[] }
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      const { groupId, ...rest } = payload;
      const response$ = of<IResponse<IGroup>>(
        await removeContactsFromGroup(groupId, rest)
      );
      response$.subscribe(() => {
        dispatch('isLoading', false, { root: true });
        dispatch('listGroupContacts', {
          isPaginated: true,
          page: 1,
          id: groupId,
          limit: 30,
        });
        dispatch('list', {
          isPaginated: true,
          page: 1,
          limit: 30,
        });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
  async listGroupContacts({ commit, dispatch }, payload) {
    try {
      dispatch('isDialogLoading', true, { root: true });
      const { id, ...rest } = payload;
      const query = qs.stringify(
        { ...rest },
        {
          skipNulls: true,
          addQueryPrefix: true,
        }
      );
      const response$ = of<IResponse<IPagination<IContact>>>(
        await groupContacts(id, query)
      );
      response$.subscribe((contacts) => {
        if (rest?.isPaginated) {
          const { page, limit, pages, total, docs } = contacts.data.paginateObj;
          commit('UPDATE_GROUP_CONTACTS', docs);
          commit('UPDATE_CONTACTS_PAGINATION', {
            page,
            pages,
            total,
            itemsPerPage: limit,
          });
        } else {
          commit('UPDATE_GROUP_CONTACTS', contacts.data);
        }
        dispatch('isDialogLoading', false, { root: true });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch('snackBarMessage', e.response.data?.message, {
          root: true,
        });
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isDialogLoading', false, { root: true });
    }
  },
  async loadMore(
    { state, commit, dispatch },
    payload: IPaginate & { limit: number; sort?: string; id: number }
  ) {
    try {
      dispatch('isLoading', true, { root: true });
      const { id, pages, page, ...rest } = payload;
      const newPage = pages && page < pages ? page + 1 : page;
      const query = qs.stringify(
        { isPaginated: true, ...rest, page: newPage },
        { skipNulls: true, addQueryPrefix: true }
      );
      const response$ = of<IResponse<IPagination<IContact>>>(
        await groupContacts(id, query)
      );
      response$.subscribe((contacts) => {
        dispatch('isLoading', false, { root: true });
        const { page, limit, pages, total, docs } = contacts.data.paginateObj;
        commit('UPDATE_GROUP_CONTACTS', [
          ...(state as any)?.groupContacts,
          ...docs,
        ]);
        commit('UPDATE_CONTACTS_PAGINATION', {
          page,
          pages,
          total,
          itemsPerPage: limit,
        });
      });
    } catch (e) {
      if (isNetworkError(e)) {
        dispatch('snackBarMessage', e?.message, {
          root: true,
        });
      } else {
        dispatch(
          'snackBarMessage',
          e.response.data?.message ?? `Unable to get more contacts`,
          {
            root: true,
          }
        );
      }
      dispatch('snackBarVisibility', true, { root: true });
      dispatch('isLoading', false, { root: true });
    }
  },
};

const getters: GetterTree<BaseState<IGroup>, RootState> = {
  getPagination: (state) => state.paginate,
  getGroupContactsPagination: (state) => state.contactsPaginate,
  getAllGroupContacts: (state) => state.groupContacts,
  getGroupDetails: (state) => state.details,
  getAllGroups: (state) => state.list,
  getResetFormValues: (state) => state.reset,
  getSearchResults: (state) => state.searchResults,
};

export const group: Module<BaseState<IGroup>, RootState> = {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
